import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Divider
} from "@mui/material";
import { Link } from "react-router-dom";
import { Eye, SquarePen, Trash2 } from "lucide-react";
import { useEvent } from "../../contexts/EventContext";
import { Modal, Spin } from "antd";
import SocialShare from "./SocialShare";
import SearchFilter from "./SearchFilter";

function formatTime(time) {
    let hours = time.split(":")[0];
    let minutes = time.split(":")[1];
    return `${hours}:${minutes}`;
}

// Function to format date (e.g., "2025-01-18" -> "18.01.2025")
function formatDate(date) {
    let [year, month, day] = date.split("-");
    return `${day}.${month}.${year}`;
}

function Events() {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const { events, handleDeleteEvent, isLoading, page, setPage, totalPages } = useEvent();

    // Handle viewing event details
    const handleViewEvent = (event) => {
        setSelectedEvent(event);
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setSelectedEvent(null);
    };

    // Function to generate .ics file
    const generateICS = (event) => {
        const formattedDate = event.event_date.replace(/-/g, ''); // Remove dashes
        const formattedStartTime = event.start_time.replace(/:/g, ''); // Remove colons
        const formattedEndTime = event.end_time.replace(/:/g, ''); // Remove colons
        const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '') + 'Z'; // Generate DTSTAMP

        const icsData = `
BEGIN:VCALENDAR
VERSION:2.0
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${timestamp}@ennovation-austria.com
DTSTAMP:${timestamp}
SUMMARY:${event.event_title}
DTSTART:${formattedDate}T${formattedStartTime}Z
DTEND:${formattedDate}T${formattedEndTime}Z
DESCRIPTION:${event?.event_description.replace(/(<([^>]+)>)/gi, '').replace(/[\r\n]/g, '\\n')}
LOCATION:${event?.event_hall_no || ''}
STATUS:CONFIRMED
SEQUENCE:0
TRANSP:OPAQUE
END:VEVENT
END:VCALENDAR
    `.trim();

        const blob = new Blob([icsData], { type: "text/calendar;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${event.event_title.replace(/[^a-z0-9]/gi, '_')}.ics`;
        link.click();
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <img
                style={{
                    width: "100%",
                    objectFit: "contain",
                    position: "fixed",
                    zIndex: -1,
                }}
                src="https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f41d5f2ba41fbbe6c5514c_AdobeStock_546815906_gruen_klein_bearbeitet_002.webp"
            />
            <Container>
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: "bold", color: "#0BD349", paddingTop: "5px" }}
                >
                    Veranstaltungen
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "flex-end", paddingBottom: "16px" }}>
                    <Link to="/dashboard/add-event">
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#fff",
                                color: "#333",
                                padding: "12px 24px",
                                fontSize: "16px",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#098B2B" },
                            }}
                        >
                            Neue Veranstaltung hinzufügen
                        </Button>
                    </Link>
                </Box>

                <SearchFilter />

                <TableContainer component={Paper} sx={{ marginBottom: 3, backgroundColor: 'rgb(28, 28, 28, 0.9)' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ backgroundColor: 'rgb(0, 0, 0, 0.6)', color: '#0BD349' }}><b>Nr.</b></TableCell>
                                <TableCell style={{ backgroundColor: 'rgb(0, 0, 0, 0.6)', color: '#0BD349' }}><b>Titel</b></TableCell>
                                <TableCell align="center" style={{ backgroundColor: 'rgb(0, 0, 0, 0.6)', color: '#0BD349' }}><b>Datum</b></TableCell>
                                <TableCell align="center" style={{ backgroundColor: 'rgb(0, 0, 0, 0.6)', color: '#0BD349' }}><b>Zeit</b></TableCell>
                                <TableCell align="center" style={{ backgroundColor: 'rgb(0, 0, 0, 0.6)', color: '#0BD349' }}><b>Location</b></TableCell>
                                <TableCell align="center" style={{ backgroundColor: 'rgb(0, 0, 0, 0.6)', color: '#0BD349' }}><b>Aktionen</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Spin spinning={isLoading} tip="Loading..." />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                events.length ? events.map((event, index) => (
                                    <TableRow key={event.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell style={{
                                            maxWidth: "200px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}>{event.event_title}</TableCell>
                                        <TableCell align="center">{formatDate(event.event_date)}</TableCell>
                                        <TableCell align="center">{`${formatTime(event.start_time)} - ${formatTime(event.end_time)}`}</TableCell>
                                        <TableCell align="center">{event?.event_hall_no || '---'}</TableCell>
                                        <TableCell align="center">
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: 'center',
                                                gap: '5px'
                                            }}>
                                                <Link style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }} to={`/dashboard/edit-event/${event.id}`}>
                                                    <SquarePen color="#13df53" size={13} />
                                                </Link>
                                                <Link style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',

                                                }} onClick={() => handleDeleteEvent(event.id)}>
                                                    <Trash2 color="#ff2009" size={15} />
                                                </Link>
                                                <Link style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',

                                                }} onClick={() => handleViewEvent(event)}>
                                                    <Eye color="#ffa600" size={15} />
                                                </Link>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="h6" sx={{ color: "#0BD349", paddingTop: "10" }}>
                                                Keine Events gefunden
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                            )}
                        </TableBody>
                    </Table>
                    <Box sx={{ display: "flex", justifyContent: "center", margin: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={(e, page) => setPage(page)} />
                    </Box>
                </TableContainer>

                {/* Modal for Event Details */}
                <Modal
                    title={<span style={{ color: '#fff' }}>Event Details</span>}
                    visible={isModalVisible}
                    onCancel={handleModalCancel}
                    style={{ top: 40 }}
                    footer={null}
                >
                    {selectedEvent && (
                        <Box sx={{ maxWidth: '600px', margin: '0 auto', padding: 2, borderRadius: '8px', overflowY: 'auto' }}>
                            {
                                selectedEvent?.event_img && (
                                    <img
                                        src={selectedEvent.event_img}
                                        alt={selectedEvent.event_title}
                                        style={{ width: '100%', borderRadius: '8px', marginBottom: '16px' }}
                                    />
                                )
                            }

                            <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '8px', textAlign: 'center' }}>
                                {selectedEvent.event_title}
                            </Typography>

                            {/* Event Details */}
                            <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                                <strong>Date:</strong> {formatDate(selectedEvent.event_date)}
                            </Typography>
                            <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                                <strong>Time:</strong> {`${formatTime(selectedEvent.start_time)} - ${formatTime(selectedEvent.end_time)}`}
                            </Typography>
                            <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                                <strong>Hall No.:</strong> {selectedEvent.event_hall_no}
                            </Typography>
                            {/* <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                                <strong>Venue:</strong> {selectedEvent.event_venue}
                            </Typography> */}
                            <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                                <strong>Program:</strong> {selectedEvent.event_program}
                            </Typography>

                            {/* Social Media Share Button */}
                            <SocialShare selectedEvent={selectedEvent} generateICS={generateICS} />

                            {/* Event Description */}
                            <Divider sx={{ marginBottom: '16px' }} />
                            <Typography variant="body2" sx={{ marginBottom: '16px' }}>
                                <strong>Description:</strong> <div dangerouslySetInnerHTML={{ __html: selectedEvent.event_description }} />
                            </Typography>
                        </Box>
                    )}
                </Modal>
            </Container>
        </Box>
    );
}

export default Events;